// storage.js - Utilities for local storage management

// Encrypt the API key before storing
const encryptApiKey = (apiKey) => {
  // This is a simple obfuscation, not true encryption
  // For a production app, consider using a more secure method
  return btoa(apiKey.split('').reverse().join(''));
};

// Decrypt the stored API key
const decryptApiKey = (encryptedKey) => {
  // Reverse the simple obfuscation
  return atob(encryptedKey).split('').reverse().join('');
};

// Save API key to localStorage with simple obfuscation
export const saveApiKey = (apiKey) => {
  if (!apiKey) return false;
  
  try {
    const encryptedKey = encryptApiKey(apiKey);
    localStorage.setItem('geek_claude_api_key', encryptedKey);
    return true;
  } catch (error) {
    console.error('Error saving API key:', error);
    return false;
  }
};

// Get the API key from localStorage
export const getApiKey = () => {
  try {
    const encryptedKey = localStorage.getItem('geek_claude_api_key');
    if (!encryptedKey) return null;
    
    return decryptApiKey(encryptedKey);
  } catch (error) {
    console.error('Error retrieving API key:', error);
    return null;
  }
};

// Remove the API key from localStorage
export const removeApiKey = () => {
  localStorage.removeItem('geek_claude_api_key');
};

// Save chat history to localStorage
export const saveChatHistory = (chatId, messages) => {
  try {
    // Get existing chat histories
    const existingChats = getChatHistories();
    
    // Update or add the current chat
    existingChats[chatId] = {
      messages,
      lastUpdated: new Date().toISOString()
    };
    
    // Store back to localStorage
    localStorage.setItem('geek_claude_chats', JSON.stringify(existingChats));
    return true;
  } catch (error) {
    console.error('Error saving chat history:', error);
    return false;
  }
};

// Get all chat histories
export const getChatHistories = () => {
  try {
    const chats = localStorage.getItem('geek_claude_chats');
    return chats ? JSON.parse(chats) : {};
  } catch (error) {
    console.error('Error retrieving chat histories:', error);
    return {};
  }
};

// Get a specific chat history
export const getChatHistory = (chatId) => {
  const histories = getChatHistories();
  return histories[chatId] || null;
};

// Delete a specific chat history
export const deleteChatHistory = (chatId) => {
  try {
    const existingChats = getChatHistories();
    
    if (existingChats[chatId]) {
      delete existingChats[chatId];
      localStorage.setItem('geek_claude_chats', JSON.stringify(existingChats));
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting chat history:', error);
    return false;
  }
};

// Generate a new chat ID
export const generateChatId = () => {
  return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get user preferences
export const getUserPreferences = () => {
  try {
    const prefs = localStorage.getItem('geek_claude_preferences');
    return prefs ? JSON.parse(prefs) : {
      darkMode: true,
      fontSize: 'medium',
      showTimestamps: false
    };
  } catch (error) {
    console.error('Error getting user preferences:', error);
    return {
      darkMode: true,
      fontSize: 'medium',
      showTimestamps: false
    };
  }
};

// Save user preferences
export const saveUserPreferences = (preferences) => {
  try {
    localStorage.setItem('geek_claude_preferences', JSON.stringify(preferences));
    return true;
  } catch (error) {
    console.error('Error saving user preferences:', error);
    return false;
  }
}; 